import classNames from "classnames";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { getProfileName } from "PFCore/helpers/profile";
import { useActivityFeedbackCreate } from "PFCore/hooks/queries/activity/use_activity_feedback_create";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { ActivityParticipant, FeedbackRoundInfo, ParticipatedRole } from "PFTypes/activity_participant";
import { useTranslation } from "react-i18next";

import { useFeedbackContext } from "./feedback_context";
import { FeedbackForm } from "./form/feedback_form";
import css from "./form/feedback_form.module.scss";
import { FeedbackFormData } from "./form/feedback_form_context";
import { useFeedbackCreateBody } from "./hooks/use_feedback_create_body";
import { useFeedbackDefaultData } from "./hooks/use_feedback_default_data";
import { useSavedFeedbackForms } from "./hooks/use_saved_feedback_forms";

const SEND_FEEDBACK_SUCCESS_ACTION_ID = getGrowlId("manager-feedback-form");

type ManagerFeedbackFormProps = {
  activeFeedbackRound: FeedbackRoundInfo | null;
  selectedParticipant: ActivityParticipant;
  selectedRole: ParticipatedRole;
  onSubmit: (data: FeedbackFormData) => void;
};

export const ManagerFeedbackForm = ({
  activeFeedbackRound,
  selectedParticipant,
  selectedRole,
  onSubmit
}: ManagerFeedbackFormProps): React.ReactElement | null => {
  const activity = useCurrentActivity();
  const { t } = useTranslation("activities", { keyPrefix: "show.feedback" });
  const { showGrowl } = useGrowlStoreActions();
  const growlError = useErrorsGrowl();

  const { setCurrentFormData } = useFeedbackContext();
  const generateCreateBody = useFeedbackCreateBody();
  const { formDefaultData, isReadOnly, provider } = useFeedbackDefaultData(activeFeedbackRound, selectedRole);
  const { participantSavedForm } = useSavedFeedbackForms();

  const { create: sendFeedback } = useActivityFeedbackCreate(activity?.id, {
    onSuccess: () => {
      showGrowl({
        id: SEND_FEEDBACK_SUCCESS_ACTION_ID,
        kind: "success",
        message: t("feedbackSent"),
        hidePreviousByTypeAndId: true,
        ttl: 5000
      });
    },
    onError: ({ response }) => growlError(response)
  });

  if (!activity) {
    return null;
  }

  const fullName = getProfileName(selectedParticipant);

  const handleSubmit = (data: FeedbackFormData) => {
    const payload = generateCreateBody(data, selectedRole.id, activity?.id, selectedParticipant.id);
    sendFeedback(payload);
    onSubmit(data);
  };

  const handleUpdate = (data: FeedbackFormData) => {
    if (!activeFeedbackRound) {
      return;
    }
    setCurrentFormData(data);
  };

  return (
    <>
      <div>
        {t("feedbackDescription", {
          profileName: fullName,
          roleName: selectedRole.name,
          engagementName: activity?.name
        })}
      </div>
      <FeedbackForm
        readOnly={isReadOnly}
        defaultData={participantSavedForm || formDefaultData}
        onSubmit={handleSubmit}
        onUpdate={handleUpdate}
        footerContent={
          <div className={classNames(css.shareInfoContainer, css.label)}>
            {isReadOnly ? t("feedbackSharedBy", { profileName: getProfileName(provider) }) : t("shareInfo")}
          </div>
        }
      />
    </>
  );
};
